import React, { useState } from 'react';
import { Search, Filter, LayoutGrid, Table, Eye, AlertCircle } from 'lucide-react';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import LeadCard from './LeadCard';
import PipelineTable from './PipelineTable';
import LeadDetailsModal from './LeadDetailsModal';
import { PIPELINE_STAGES } from '../../shared/constants';

const PipelinePage: React.FC = () => {
  const { data: allLeads, isLoading, error, isAuthenticated, updateRecord } = useGoogleSheets('pipeline-leads');
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [currentView, setCurrentView] = useState<'kanban' | 'table'>('kanban');
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [draggedLead, setDraggedLead] = useState<any>(null);

  const filteredLeads = allLeads.filter(lead => {
    const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.industry || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = stageFilter === 'all' || lead.stage === stageFilter;
    return matchesSearch && matchesStage;
  });

  const getLeadsByStage = (stageId: string) => {
    return filteredLeads.filter(lead => {
      const leadStage = (lead.stage || '').toLowerCase().replace(/\s+/g, '_');
      return leadStage === stageId;
    });
  };

  const handleViewDetails = (lead: any) => {
    setSelectedLead(lead);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLead(null);
  };

  const handleDragStart = (e: React.DragEvent, lead: any) => {
    setDraggedLead(lead);
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetStage: string) => {
    e.preventDefault();
    
    if (!draggedLead || !draggedLead._rowIndex) return;
    
    const currentStage = (draggedLead.pipeline_stage || draggedLead.stage || '').toLowerCase().replace(/\s+/g, '_');
    const newStageId = targetStage;
    
    if (currentStage === newStageId) {
      setDraggedLead(null);
      return;
    }
    
    console.log('[PipelinePage] Dropping lead:', {
      leadId: draggedLead._rowIndex,
      currentStage,
      newStageId,
      draggedLead
    });
    
    try {
      // Convert stage ID back to display format
      const stageDisplayNames = {
        'engaged': 'Engaged',
        'interested': 'Interested',
        'qualified': 'Qualified',
        'meeting_booked': 'Meeting Booked',
        'proposal_sent': 'Proposal Sent',
        'closed_won': 'Closed Won',
        'closed_lost': 'Closed Lost'
      };
      
      const displayName = stageDisplayNames[newStageId as keyof typeof stageDisplayNames] || newStageId;
      
      console.log('[PipelinePage] Updating stage to:', displayName);
      
      await updateRecord(draggedLead._rowIndex, 'pipeline_stage', displayName);
      
      console.log('[PipelinePage] Stage updated successfully');
    } catch (error) {
      console.error('[PipelinePage] Failed to update lead stage:', error);
    } finally {
      setDraggedLead(null);
    }
  };
  // Only show loading if we're loading AND have no data to display
  if (isLoading && allLeads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 text-lg mb-2">Loading data...</div>
          <div className="text-gray-400 text-sm">Please Make Sure The System Is Connected</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex items-center justify-end">
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setCurrentView('kanban')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              currentView === 'kanban'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <LayoutGrid size={16} />
            <span>Kanban View</span>
          </button>
          <button
            onClick={() => setCurrentView('table')}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              currentView === 'table'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={16} />
            <span>Table View</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Stages</option>
                {PIPELINE_STAGES.map(stage => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {currentView === 'kanban' ? (
        /* Kanban View */
        <div className="overflow-x-auto">
          <div className="flex space-x-6 min-w-max pb-4">
            {PIPELINE_STAGES.map(stage => {
              const stageLeads = getLeadsByStage(stage.id);
              return (
                <div key={stage.id} className="flex-shrink-0 w-80">
                  <div 
                    className="bg-white rounded-xl border border-gray-200 h-full"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, stage.id)}
                  >
                    {/* Stage Header */}
                    <div className="p-4 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900">{stage.name}</h3>
                        <span className="text-sm font-medium text-gray-500">{stageLeads.length}</span>
                      </div>
                      <p className="text-xs text-gray-500">{stage.description}</p>
                    </div>
                    
                    {/* Stage Content */}
                    <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                      {stageLeads.map(lead => (
                        <LeadCard
                          key={lead._rowIndex || lead.id}
                          lead={lead}
                          onClick={() => handleViewDetails(lead)}
                          onDragStart={handleDragStart}
                        />
                      ))}
                      {stageLeads.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <div className="text-sm">No leads in this stage</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Table View */
        <PipelineTable leads={filteredLeads} onViewDetails={handleViewDetails} />
      )}

      {/* Lead Details Modal */}
      {showModal && selectedLead && (
        <LeadDetailsModal
          lead={selectedLead}
          onClose={handleCloseModal}
          onUpdateLead={updateRecord}
        />
      )}

      {filteredLeads.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No pipeline leads found</div>
          <div className="text-gray-400 text-sm mt-1">
            {searchTerm || stageFilter !== 'all' ? 'Try adjusting your filters' : 'Pipeline leads will appear here once they interact with your AI Sales Team'}
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelinePage;